import React, { useMemo, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import DetailField from "./DetailField.jsx";

const COLLAPSE_THRESHOLD = 8;

/** Map catalog status → Catalog Items tab index (`t`). */
function tabForStatus(status) {
    switch (status) {
        case "Awaiting Pickup":
            return 0;
        case "Inspection":
            return 2;
        case "Putaway":
            return 3;
        case "Listed":
            return 4;
        case "Missing":
            return 5;
        default:
            return 6; // Catch All (all statuses)
    }
}

function catalogItemsHref({ q, fromInvoice, status }) {
    const params = new URLSearchParams();
    params.set("t", String(tabForStatus(status)));
    if (q) params.set("q", q);
    if (fromInvoice) params.set("fromInvoice", String(fromInvoice));
    return `/catalog-items?${params.toString()}`;
}

export default function LinkedCatalogItems({
    catalogItems = [],
    totalAmount,
    formatMoney,
    invoiceId,
}) {
    const [expanded, setExpanded] = useState(false);
    const items = catalogItems || [];
    const count = items.length;
    const needsCollapse = count > COLLAPSE_THRESHOLD;
    const visible = needsCollapse && !expanded ? items.slice(0, COLLAPSE_THRESHOLD) : items;

    const openAllHref = useMemo(() => {
        if (!count) return "/catalog-items";
        // Prefer Catch All when opening the full set so mixed statuses aren't hidden by a status tab.
        const params = new URLSearchParams({ t: "6" });
        if (invoiceId) params.set("fromInvoice", String(invoiceId));
        return `/catalog-items?${params.toString()}`;
    }, [count, invoiceId]);

    if (!count) {
        return <DetailField label="Line items" value="" />;
    }

    return (
        <DetailField label="Line items">
            <Typography sx={{ fontSize: 14, color: "#1c2430", fontWeight: 600, mb: 1 }}>
                {count} item{count === 1 ? "" : "s"}
                {totalAmount != null && formatMoney
                    ? ` · ${formatMoney(totalAmount)}`
                    : ""}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                {visible.map((c) => {
                    const label = c.itemId || c.parserItemId || c._id;
                    const q = c.itemId || c.parserItemId || "";
                    return (
                        <Chip
                            key={c._id}
                            size="small"
                            label={label}
                            component={RouterLink}
                            to={catalogItemsHref({
                                q,
                                fromInvoice: invoiceId,
                                status: c.status,
                            })}
                            clickable
                            sx={{
                                fontSize: 12,
                                fontWeight: 600,
                                textDecoration: "none",
                                "&:hover": { backgroundColor: "#ecfdf5" },
                            }}
                        />
                    );
                })}
            </Box>
            {needsCollapse ? (
                <Button
                    size="small"
                    onClick={() => setExpanded((v) => !v)}
                    startIcon={
                        expanded ? (
                            <ExpandLessOutlinedIcon fontSize="small" />
                        ) : (
                            <ExpandMoreOutlinedIcon fontSize="small" />
                        )
                    }
                    sx={{ textTransform: "none", fontWeight: 600, px: 0, minWidth: 0 }}
                >
                    {expanded ? "Show fewer" : `Show all ${count} items`}
                </Button>
            ) : null}
            <Button
                component={RouterLink}
                to={openAllHref}
                size="small"
                sx={{ textTransform: "none", fontWeight: 600, mt: 0.5, display: "block" }}
            >
                Open in catalog items
            </Button>
        </DetailField>
    );
}
